import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Catalog from "@/pages/Catalog";
import Reports from "@/pages/Reports";
import Documents from "@/pages/Documents";
import PhysicalCatalogs from "@/pages/PhysicalCatalogs";
import UserGuide from "@/pages/UserGuide";
import PrivateSection from "@/pages/PrivateSection";
import Settings from "@/pages/Settings";
import Projetos from "@/pages/Projetos";
import PropostaInvestimento from "@/pages/PropostaInvestimento";
import Profile from "@/pages/Profile";
import Statistics from "@/pages/Statistics";
import BulkUpload from "@/pages/BulkUpload";
import Incidencias from "@/pages/Incidencias";
import Promocao from "@/pages/Promocao";
import GabineteDetail from "@/pages/GabineteDetail";
import SalesAnalysis from "@/pages/SalesAnalysis";
import ChatbotPage from "@/pages/ChatbotPage";
import Agenda from "@/pages/Agenda";
import RappelPage from "@/pages/RappelPage";
import Comissoes from "@/pages/Comissoes";
import Tabelas from "@/pages/Tabelas";
import Encomendas from "@/pages/Encomendas";
import Catalogos from "@/pages/Catalogos";
import Reclamacoes from "@/pages/Reclamacoes";
import ReclamacaoWizard from "@/pages/ReclamacaoWizard";
import ReclamacaoDetail from "@/pages/ReclamacaoDetail";
import Precos from "@/pages/Precos";
import PrecoDetail from "@/pages/PrecoDetail";
import Login from "@/pages/Login";
import SplashScreen from "@/components/SplashScreen";

// Blocks every screen (including "/") until a valid session is confirmed. Any attempt to
// open an internal URL while logged out redirects straight to /login, remembering the
// originally requested page so login can send the user back there afterwards.
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-loading">
        <LoadingSpinner size={48} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:clientId" element={<ClientDetail />} />
        <Route path="catalog" element={<Catalog />} />
        <Route path="documents" element={<Documents />} />
        <Route path="physical-catalogs" element={<PhysicalCatalogs />} />
        <Route path="reports/:clientId" element={<Reports />} />
        <Route path="private" element={<PrivateSection />} />
        <Route path="settings" element={<Settings />} />
        <Route path="projetos" element={<Projetos />} />
        <Route path="projetos/propostas/:proposalId" element={<PropostaInvestimento />} />
        <Route path="profile" element={<Profile />} />
        <Route path="statistics" element={<Statistics />} />
        <Route path="bulk-upload" element={<BulkUpload />} />
        <Route path="incidencias" element={<Incidencias />} />
        <Route path="promocao" element={<Promocao />} />
        <Route path="promocao/:id" element={<GabineteDetail />} />
        <Route path="sales-analysis" element={<SalesAnalysis />} />
        <Route path="agenda" element={<Agenda />} />
        <Route path="rappel" element={<RappelPage />} />
        <Route path="comissoes" element={<Comissoes />} />
        <Route path="tabelas" element={<Tabelas />} />
        <Route path="encomendas" element={<Encomendas />} />
        <Route path="catalogos" element={<Catalogos />} />
        <Route path="reclamacoes" element={<Reclamacoes />} />
        <Route path="reclamacoes/nova" element={<ReclamacaoWizard />} />
        <Route path="reclamacoes/:complaintId/editar" element={<ReclamacaoWizard />} />
        <Route path="reclamacoes/:complaintId" element={<ReclamacaoDetail />} />
        <Route path="precos" element={<Precos />} />
        <Route path="precos/:productId" element={<PrecoDetail />} />
        <Route path="visitas" element={<Navigate to="/agenda" replace />} />
      </Route>
      <Route path="/guide" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />
      <Route path="/assistant" element={<ProtectedRoute><ChatbotPage /></ProtectedRoute>} />
      <Route path="/login" element={<Login />} />
    </Routes>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <AuthProvider>
      <SettingsProvider>
        <div className="App">
          {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster position="top-right" richColors offset={80} />
        </div>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;

